/**
 * WHAT CUSTOMERS SAY ABOUT THE SHOP — the block under the description.
 *
 * The owner (2026-10-05): "Add some reviews on the shop because it makes it
 * more reliable — we couldn't import the reviews from Google. Show them on
 * the product page somewhere if the review isn't on the product itself."
 *
 * So: the business's REAL reviews, transcribed by the business from its own
 * Google listing, each with the reviewer's first name, the date, the stars,
 * the text and a visible source label. When this product has reviews of its
 * own they come first (`ReviewsSection`, mounted above this in the page) and
 * this block follows; when it has none, this is the reviews block.
 *
 * NO REQUEST ON FIRST PAINT. The count, the mean and the three cards arrive
 * inside the salon payload the page already loads (`salon.shopReviews`), so
 * the block paints with the product and shifts nothing above the fold — it
 * sits below the description. "See all N" is the one request, made on tap.
 *
 * It renders nothing at all when the shop has no transcribed reviews, so a
 * shop that never used the portal panel sees its page exactly as before.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StarsRow } from './ReviewCard';
import ShopReviewCard, { allFromGoogle, shopReviewsCountLine } from './ShopReviewCard';
import ShopReviewsModal from './ShopReviewsModal';
import sectionStyles from './reviewsSection.module.css';
import styles from './shopReviewsBlock.module.css';

const ShopReviewsBlock = ({ shopReviews, salonSlug, shopName, t }) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language;
  const [open, setOpen] = useState(false);

  const count = Number(shopReviews?.count) || 0;
  const sample = Array.isArray(shopReviews?.sample) ? shopReviews.sample.filter(Boolean) : [];
  if (count <= 0 || sample.length === 0) return null;

  const average = Number(shopReviews?.average) || 0;
  /* The list these were copied from — the first sampled review that names it. `http(s)` only:
   * this is an address that arrived in a payload, and it is about to become an href. */
  const googleSourceUrl = allFromGoogle(shopReviews)
    ? (sample.map((r) => r?.sourceUrl).find((u) => typeof u === 'string' && /^https?:\/\//i.test(u)) || null)
    : null;
  const title = shopName
    ? t('shopReviews.title', { defaultValue: 'What customers say about {{shop}}', shop: shopName })
    : t('shopReviews.titleNoShop', { defaultValue: 'What customers say' });

  return (
    <section
      className={`${sectionStyles.section} ${styles.section}`}
      data-testid="product-detail-shop-reviews"
      data-count={count}
    >
      <div className={sectionStyles.header}>
        <div className={sectionStyles.headerMain}>
          <h2 className={sectionStyles.title} data-testid="product-detail-shop-reviews-title">{title}</h2>
          <div className={sectionStyles.avgRow}>
            <span className={sectionStyles.avgValue}>{average.toFixed(1)}</span>
            <StarsRow value={average} size={20} t={t} testId="product-detail-shop-reviews-average-stars" />
            <span className={sectionStyles.avgCount} data-testid="product-detail-shop-reviews-count">
              ({shopReviewsCountLine(shopReviews, t)})
            </span>
          </div>
          {allFromGoogle(shopReviews) && (
            <p className={styles.note} data-testid="product-detail-shop-reviews-note">
              {t('shopReviews.transcribedNote', {
                defaultValue: 'Real reviews, written on Google and transcribed by the shop — names and dates as published.',
              })}
            </p>
          )}
          {/* "IF POSSIBLE TO ADD LINK OF SEE US ON GOOGLE (which opens the list of ratings the
            * one that you took the reviews from it)" — 2026-10-07. The address is not written
            * here and not configured anywhere: every transcribed review already carries the
            * `sourceUrl` it was copied from, which IS that list, so the link is taken from the
            * reviews themselves. Nothing to keep in step, nothing to invent, and it appears
            * only for a shop whose reviews actually came from Google. */}
          {googleSourceUrl && (
            <a
              className={styles.googleLink}
              href={googleSourceUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              data-testid="product-detail-shop-reviews-google-link"
            >
              {t('shopReviews.seeOnGoogle', { defaultValue: 'See our reviews on Google' })}
            </a>
          )}
        </div>
        {/* SAYS ITS OWN NAME IN THE MOVEMENT RECORD (2026-10-14, the owner: "make sure we have full
          * coverage of movements in the website"). A tap is named by `data-move` and only failing
          * that by the test id under a known prefix (utils/shopMovement.js tapTargetOf) — so the
          * name in thirty days of `shop_tap` rows was a side effect of a test id, and renaming the
          * id for a test's sake would have taken the column away with nothing going red. These are
          * the names the test ids have been producing, written down. The wide copy keeps its own
          * name: it is the one a narrow phone never shows. */}
        {count > sample.length && (
          <button
            type="button"
            className={sectionStyles.viewAllBtn}
            onClick={() => setOpen(true)}
            data-testid="product-detail-shop-reviews-see-all"
            data-move="shop_reviews_see_all"
          >
            {t('shopReviews.seeAll', { defaultValue: 'See all {{n}}', n: count, count })}
          </button>
        )}
      </div>

      <div className={sectionStyles.list}>
        {sample.map((review) => (
          <ShopReviewCard
            key={review.id}
            review={review}
            t={t}
            locale={locale}
            testIdPrefix="product-detail-shop-review"
          />
        ))}
      </div>

      {count > sample.length && (
        <button
          type="button"
          className={sectionStyles.viewAllWide}
          onClick={() => setOpen(true)}
          data-testid="product-detail-shop-reviews-see-all-wide"
          data-move="shop_reviews_see_all_wide"
        >
          {t('shopReviews.seeAll', { defaultValue: 'See all {{n}}', n: count, count })}
        </button>
      )}

      {open && (
        <ShopReviewsModal
          open={open}
          salonSlug={salonSlug}
          shopName={shopName}
          summary={shopReviews}
          onClose={() => setOpen(false)}
          t={t}
        />
      )}
    </section>
  );
};

export default ShopReviewsBlock;
